import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { GraphStateService, OtpDtpGraphPayload } from "../core-module/services/state/graph-state.service";
import { renderOtpDeratingGraph } from "../shared-module/graph-utils/otp-derating-graph";

// X axis max temperature
const X_MAX_TEMP = 120;

@Component({
  standalone: false,
  selector: "app-otp-dtp-graph",
  templateUrl: "./otp-dtp-graph.component.html",
  styleUrls: ["./otp-dtp-graph.component.scss"],
})
export class OtpDtpGraphComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private static readonly DEFAULT_MIN_DIM_LEVEL = 10;

  // Kept as explicit public state for template/tests and API compatibility.
  readonly inputXRange: [number, number] = [0, X_MAX_TEMP];
  readonly inputYRange: [number, number] = [0, 100];

  // Default data (83-87 range)
  data: any[] = [
    { x: 0,          y: 100 },
    { x: 83,         y: 100 },
    { x: 87,         y: OtpDtpGraphComponent.DEFAULT_MIN_DIM_LEVEL },
    { x: X_MAX_TEMP, y: OtpDtpGraphComponent.DEFAULT_MIN_DIM_LEVEL },
  ];

  constructor(private graphState: GraphStateService) {}

  ngOnInit() {
    this.buildGraph();

    this.graphState.getOtpDtpGraphData
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        if (payload) {
          this.data = this.buildDataPoints(payload);
        }
        this.buildGraph();
      });
  }

  /**
   * Build 4-point derating curve for DTP:
   *  [0] x=0,        y=100%       (cold temperature, full brightness)
   *  [1] x=dimStart, y=100%       (83 or 92 — dimming starts)
   *  [2] x=dimStop,  y=minDimLevel (87 or 97 — max derating level from schema)
   *  [3] x=X_MAX,    y=minDimLevel (stays at min dim)
   */
  private buildDataPoints(payload: OtpDtpGraphPayload): any[] {
    const start = Math.min(X_MAX_TEMP, Math.max(0, payload.dimStart));
    const stop  = Math.min(X_MAX_TEMP, Math.max(0, payload.dimStop));
    const dimLevel = (typeof payload.minDimLevel === "number" && !isNaN(payload.minDimLevel))
      ? payload.minDimLevel
      : OtpDtpGraphComponent.DEFAULT_MIN_DIM_LEVEL;

    return [
      { x: 0,          y: 100 },
      { x: start,      y: 100 },
      { x: stop,       y: dimLevel },
      { x: X_MAX_TEMP, y: dimLevel },
    ];
  }

  private buildGraph() {
    renderOtpDeratingGraph(this.data, {
      svgId: "otpDtpGraphSvg",
      gradientId: "otpDtpGradient",
      xAxisLabel: "Temperature (&deg;C)",
      xAxisLabelAsHtml: true,
      chartHeight: 300,
      xDomain: this.inputXRange,
      yDomain: this.inputYRange,
      xTicks: 12,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
